import { Component } from '@theme/component';

/**
 * Counts each stat number up from zero the first time the band scrolls into view.
 * Text around the first number is kept as typed ("10 Mn+", "50,000", "100%").
 * Values with no digits, and visitors who prefer reduced motion, are left static.
 *
 * @typedef {object} AboutStatsRefs
 * @property {HTMLElement[]} [numbers] - Visible (aria-hidden) number spans, one per stat.
 *
 * @extends {Component<AboutStatsRefs>}
 */
class AboutStatsComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<number>} */
  #frames = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#stop();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #start() {
    if (this.dataset.animate !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = (this.refs.numbers ?? []).filter((element) => parseStat(element.dataset.value));
    if (!targets.length) return;

    for (const element of targets) {
      const stat = parseStat(element.dataset.value);
      if (stat) element.textContent = formatStat(stat, 0);
    }

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#observer = null;
        for (const element of targets) this.#count(element);
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = null;
    for (const frame of this.#frames) cancelAnimationFrame(frame);
    this.#frames.clear();

    // Restore final values so a stopped animation never leaves partial numbers
    for (const element of this.refs.numbers ?? []) {
      if (element.dataset.value !== undefined) element.textContent = element.dataset.value;
    }
  }

  /**
   * @param {HTMLElement} element
   */
  #count(element) {
    const stat = parseStat(element.dataset.value);
    if (!stat) return;

    const duration = 1600;
    const start = performance.now();

    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      element.textContent = progress < 1 ? formatStat(stat, stat.value * eased) : stat.original;
      if (progress < 1) {
        const frame = requestAnimationFrame(step);
        this.#frames.add(frame);
      }
    };

    this.#frames.add(requestAnimationFrame(step));
  }
}

/**
 * @typedef {object} ParsedStat
 * @property {string} original - The value exactly as entered.
 * @property {string} prefix - Text before the number.
 * @property {number} value - The numeric value.
 * @property {number} decimals - Decimal places to keep while counting.
 * @property {boolean} grouping - Whether the number used thousands separators.
 * @property {string} suffix - Text after the number.
 */

/**
 * @param {string | undefined} text
 * @returns {ParsedStat | null}
 */
function parseStat(text) {
  if (!text) return null;
  const match = text.match(/^(\D*?)(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?([\s\S]*)$/);
  if (!match) return null;

  const [, prefix = '', integer = '', fraction = '', suffix = ''] = match;
  const value = Number(`${integer.replace(/,/g, '')}${fraction}`);
  if (!Number.isFinite(value)) return null;

  return {
    original: text,
    prefix,
    value,
    decimals: fraction ? fraction.length - 1 : 0,
    grouping: integer.includes(','),
    suffix,
  };
}

/**
 * @param {ParsedStat} stat
 * @param {number} current
 */
function formatStat(stat, current) {
  const number = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: stat.decimals,
    maximumFractionDigits: stat.decimals,
    useGrouping: stat.grouping,
  }).format(current);
  return `${stat.prefix}${number}${stat.suffix}`;
}

if (!customElements.get('about-stats-component')) {
  customElements.define('about-stats-component', AboutStatsComponent);
}
